import axios from 'axios';
import io from 'socket.io-client';

// Configuración de las URLs de la API
const API_URLS = {
    production: {
        http: 'https://centro-infantil-mijardin.onrender.com/api/chat',
        ws: 'wss://centro-infantil-mijardin.onrender.com'
    },
    development: {
        http: 'http://localhost:5000/api/chat',
        ws: 'ws://localhost:5000'
    }
};

// Determinar las URLs base según el entorno
const API_URL = process.env.NODE_ENV === 'production' ? API_URLS.production.http : API_URLS.development.http;
const WS_URL = process.env.NODE_ENV === 'production' ? API_URLS.production.ws : API_URLS.development.ws;

// Configuración global de axios
axios.defaults.withCredentials = true;

// Configuración de Socket.IO
export const socket = io(WS_URL, {
    withCredentials: true,
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    path: '/socket.io'
});

// Manejadores de eventos del socket
socket.on('connect', () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user?.id) {
        socket.emit('user_connected', { userId: user.id });
    }
});

socket.on('connect_error', (error) => {
});

socket.on('disconnect', () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user?.id) {
        socket.emit('user_disconnected', { userId: user.id });
    }
});

// Función para obtener todos los usuarios
export const obtenerTodosLosUsuarios = async () => {
    try {
        const response = await axios.get(`${API_URL}/usuarios`, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
            }
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Función para unirse a un chat específico
export const joinChat = (userId, otherUserId) => {
    const chatId = [userId, otherUserId].sort().join('_');
    socket.emit('join_chat', chatId);
};

// Función para suscribirse a mensajes nuevos
export const subscribeToMessages = (callback) => {
    socket.on('receive_message', (message) => {
        callback(message);
    });

    return () => {
        socket.off('receive_message');
    };
};

// Función para obtener mensajes del chat
export const obtenerMensajesChat = async (userId) => {
    try {
        if (!userId) throw new Error('Se requiere el ID del usuario');

        const response = await axios.get(
            `${API_URL}/mensajes/${userId}`,
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const enviarMensaje = async (userId, datos) => {
    try {
        if (!userId) throw new Error('Se requiere el ID del usuario');

        // Primero, emitir el mensaje en tiempo real sin cifrar
        const messageData = {
            id_emisor: userId,
            id_receptor: datos.id_receptor,
            contenido: datos.contenido,
            fecha_envio: datos.fecha_envio,
            hora: datos.hora,
            estado: datos.estado
        };
        
        // Emitir el mensaje para que se muestre en tiempo real
        socket.emit('send_message', messageData);

        // Luego, guardar el mensaje cifrado en la base de datos
        const response = await axios.post(
            `${API_URL}/enviar/${userId}`,
            {
                id_receptor: datos.id_receptor,
                contenido: datos.contenido,
                estado: datos.estado,
                fecha_envio: datos.fecha_envio,
                hora: datos.hora
            },
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

// Función para obtener el número de mensajes no leídos
export const obtenerMensajesNoLeidos = async (userId) => {
    try {
        const response = await axios.get(
            `${API_URL}/mensajes-no-leidos/${userId}`,
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Función para marcar mensajes como leídos
export const marcarMensajesComoLeidos = async (userId, emisorId) => {
    try {
        const response = await axios.post(
            `${API_URL}/marcar-leidos`,
            {
                id_receptor: userId,
                id_emisor: emisorId
            },
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            }
        );

        if (response.data.success) {
            // Emitir evento para actualizar el estado de los mensajes
            socket.emit('messages_read', {
                userId,
                emisorId,
                unreadCount: response.data.unreadCount
            });
        }

        return response.data;
    } catch (error) {
        throw error;
    }
};

// Función para suscribirse a nuevos mensajes y conteo de no leídos
export const subscribeToNewMessages = (userId, callback) => {
    // Escuchar nuevos mensajes
    socket.on(`new_message_${userId}`, (data) => {
        callback({ ...data, type: 'new_message' });
    });

    // Escuchar actualizaciones de estado de mensajes
    socket.on(`messages_read_${userId}`, (data) => {
        callback({ ...data, type: 'messages_read' });
    });

    // Escuchar cambios en el contador de mensajes no leídos
    socket.on(`unread_count_${userId}`, (count) => {
        callback({ type: 'unread_count', count });
    });

    return () => {
        socket.off(`new_message_${userId}`);
        socket.off(`messages_read_${userId}`);
        socket.off(`unread_count_${userId}`);
    };
};

// Función para obtener el estado de conexión de un usuario
export const getUserConnectionStatus = (userId, callback) => {
    socket.emit('get_user_status', userId);
    socket.on(`user_status_${userId}`, (status) => {
        callback(status);
    });
};

// Función para suscribirse a cambios de estado de usuarios
export const subscribeToUserStatus = (callback) => {
    socket.on('user_status_change', (data) => {
        callback(data);
    });

    socket.on('users_connected', (usuarios) => {
        usuarios.forEach(usuario => {
            callback({
                userId: usuario.userId,
                status: usuario.status
            });
        });
    });

    return () => {
        socket.off('user_status_change');
        socket.off('users_connected');
    };
};

